import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import InfraServicesCard from "@/components/InfraServicesCard";
import "./Admin.css";

/* ============================================================
   Admin Paneli — referans HTML (admin_ref.html) bire bir
   ============================================================ */

const api = {
  users: () => http.get("/admin/users").then((r) => r.data),
  pending: () => http.get("/admin/users/pending").then((r) => r.data),
  sessions: (active) =>
    http.get("/admin/sessions", { params: { active_only: active } }).then((r) => r.data),
  approve: (id) => http.post(`/admin/users/${id}/approve`).then((r) => r.data),
  reject: (id) => http.post(`/admin/users/${id}/reject`).then((r) => r.data),
  remove: (id) => http.delete(`/admin/users/${id}`).then((r) => r.data),
  revoke: (id) => http.delete(`/admin/sessions/${id}`).then((r) => r.data),
  testFlow: (username) =>
    http.post("/admin/test-login-flow", { username }).then((r) => r.data),
  backup: () =>
    http
      .get("/admin/backup", { responseType: "blob" })
      .then((r) => ({ blob: r.data, name: r.headers["content-disposition"] })),
};

/* ============================================================
   ICONS (lightweight inline svg — referans tasarımına bire bir)
   ============================================================ */
const I = {
  infra: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /><path d="M7 7h.01M7 17h.01" /></svg>
  ),
  db: (
    <svg viewBox="0 0 24 24"><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6" /><path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" /></svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24"><path d="M12 2 4 5v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V5l-8-3z" /><circle cx="12" cy="10" r="2.5" /><path d="M8 16c0-2 1.8-3 4-3s4 1 4 3" /></svg>
  ),
  radio: (
    <svg viewBox="0 0 24 24"><path d="M5 12a7 7 0 0 1 7-7M5 12a7 7 0 0 0 7 7M2 12a10 10 0 0 1 10-10" /><circle cx="12" cy="12" r="2" /></svg>
  ),
  flask: (
    <svg viewBox="0 0 24 24"><path d="M9 3v6L4 19a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-10V3" /><path d="M9 3h6" /></svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 11l5 4 5-4M5 21h14" /></svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24"><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" strokeLinecap="round"><path d="M6 6l12 12M18 6l-12 12" /></svg>
  ),
  send: (
    <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
  ),
};

/* ============================================================
   Helpers
   ============================================================ */
function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const day = String(d.getDate()).padStart(2, "0");
    const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
    return `${day} ${months[d.getMonth()]} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return s; }
}

function relativeMinutes(s) {
  if (!s) return null;
  const diff = (Date.now() - new Date(s).getTime()) / 60000;
  return Number.isFinite(diff) ? Math.max(0, diff) : null;
}

function ActivityPill({ lastActive }) {
  const min = relativeMinutes(lastActive);
  if (min === null) return <span className="dash">—</span>;
  if (min < 15) {
    return (
      <span className="pill grn"><span className="pd" />Aktif</span>
    );
  }
  if (min < 60) return <span className="pill mut"><span className="pd" />Yakında</span>;
  return <span className="pill mut"><span className="pd" />Pasif</span>;
}

function Avatar({ name }) {
  const ch = (name || "?").charAt(0).toUpperCase();
  return <div className="ava">{ch}</div>;
}

/* ============================================================
   Login Flow Test (small admin diagnostic, kept but restyled)
   ============================================================ */
function LoginFlowTestCard() {
  const [username, setUsername] = useState("");
  const [result, setResult] = useState(null);
  const mut = useMutation({
    mutationFn: () => api.testFlow(username.trim().replace(/^@+/, "")),
    onSuccess: (d) => setResult(d),
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <section className="card">
      <span className="cb3" /><span className="cb4" />
      <div className="ch-head">
        <div className="ch-ic">{I.flask}</div>
        <div className="ch-tt">
          <h2>Login Flow Testi</h2>
          <div className="sub">OTP akışı uçtan uca · DB → Status → Telegram → Bot → Mesaj</div>
        </div>
        <div className="ch-note">Gerçek OTP üretilir</div>
      </div>
      <div className="lf-body">
        <p className="lf-desc">
          Bir kullanıcı için tüm OTP akışını adım adım test eder.{" "}
          <b>Gerçek bir OTP üretilir</b> (5 dk geçerli), test edilen kullanıcı isterse login olabilir.
        </p>
        <div className="lf-input-row">
          <div className="lf-input-wrap">
            <span className="at">@</span>
            <input
              type="text"
              className="lf-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && username.trim() && mut.mutate()}
              placeholder="kullaniciadi (örn paranoya)"
              data-testid="login-flow-test-username"
            />
          </div>
          <button
            type="button"
            className="btn btn-orange"
            disabled={mut.isPending || !username.trim()}
            onClick={() => mut.mutate()}
            data-testid="login-flow-test-run"
          >
            {I.send}
            {mut.isPending ? "Test ediliyor..." : "Test Et"}
          </button>
        </div>

        {result && (
          <div className="lf-result" data-testid="login-flow-test-result">
            {result.steps?.map((s, i) => (
              <div
                key={i}
                className={`lf-step ${s.ok ? "ok" : "no"}`}
                data-testid={`login-flow-step-${i}`}
              >
                <span className="lf-ic">{s.ok ? I.check : I.x}</span>
                <div>
                  <span className="lf-name">{s.name}</span>
                  {s.detail && <span className="lf-detail">{s.detail}</span>}
                </div>
              </div>
            ))}
            <div
              className={`lf-summary ${result.otp_sent ? "ok" : "no"}`}
              data-testid="login-flow-test-summary"
            >
              {result.summary}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ============================================================
   PAGE
   ============================================================ */
export default function Admin() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [activeOnly, setActiveOnly] = useState(true);

  // Periyodik son aktivite render'ı için saat tetikleyici
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const { data: users } = useQuery({ queryKey: ["admin", "users"], queryFn: api.users });
  const { data: pending } = useQuery({ queryKey: ["admin", "pending"], queryFn: api.pending });
  const { data: sessions } = useQuery({
    queryKey: ["admin", "sessions", activeOnly],
    queryFn: () => api.sessions(activeOnly),
    refetchInterval: 20_000,
  });

  const approveMut = useMutation({
    mutationFn: api.approve,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Kullanıcı onaylandı");
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rejectMut = useMutation({
    mutationFn: api.reject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Reddedildi");
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: api.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Kullanıcı silindi");
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const revokeMut = useMutation({
    mutationFn: api.revoke,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success("Oturum sonlandırıldı");
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const downloadBackup = async () => {
    try {
      const { blob } = await api.backup();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `terminalex-backup-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Yedek indirildi");
    } catch (e) {
      toast.error(errMsg(e, "Yedek alınamadı"));
    }
  };

  if (me && me.role !== "admin") {
    return (
      <div className="adm-page">
        <section className="card">
          <span className="cb3" /><span className="cb4" />
          <div style={{ padding: 28, textAlign: "center" }}>
            <h2 style={{ marginBottom: 6 }}>Yetki yok</h2>
            <p style={{ color: "var(--muted)" }}>Bu sayfa sadece admin&apos;lere açık.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="adm-page" data-testid="admin-page">
      {/* ===== Altyapı & Abonelikler — InfraServicesCard kendi .card yapısını render eder ===== */}
      <InfraServicesCard />

      {/* ===== Veritabanı Yedeği ===== */}
      <section className="card" data-testid="db-backup-card">
        <span className="cb3" /><span className="cb4" />
        <div className="actcard">
          <div className="ch-ic">{I.db}</div>
          <div className="txt">
            <h2>Veritabanı Yedeği</h2>
            <p>
              Tüm koleksiyonları ZIP olarak indir. API anahtarları yedek
              dosyasında <b>maskelenir</b> — <span className="lock">dosya
              başkasının eline geçse bile key&apos;ler okunmaz.</span>
            </p>
          </div>
          <button
            type="button"
            className="btn btn-orange"
            style={{ height: 42, padding: "0 18px", fontSize: 14 }}
            onClick={downloadBackup}
            data-testid="download-backup-btn"
          >
            {I.download} ZIP indir
          </button>
        </div>
      </section>

      {/* ===== Login Flow Testi ===== */}
      <LoginFlowTestCard />

      {/* ===== Bekleyen Davetler ===== */}
      {(pending?.length ?? 0) > 0 && (
        <section className="card" data-testid="pending-invites-card">
          <span className="cb3" /><span className="cb4" />
          <div className="ch-head">
            <div className="ch-ic">{I.shield}</div>
            <div className="ch-tt">
              <h2>
                Bekleyen Davetler <span className="n">({pending.length})</span>
              </h2>
              <div className="sub">Telegram&apos;dan da <b>onaylayabilirsin</b></div>
            </div>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Kullanıcı</th>
                  <th>Chat ID</th>
                  <th>Kayıt</th>
                  <th className="r">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((u) => (
                  <tr key={u.id} data-testid={`pending-row-${u.username}`}>
                    <td>
                      <div className="svc">
                        <Avatar name={u.username} />
                        <span className="uname">@{u.username}</span>
                      </div>
                    </td>
                    <td><span className="mono" style={{ color: "var(--muted)" }}>{u.telegram_chat_id}</span></td>
                    <td><span className="dt">{fmtDate(u.created_at)}</span></td>
                    <td className="r">
                      <div className="row-act">
                        <button
                          type="button"
                          className="ico paid"
                          onClick={() => approveMut.mutate(u.id)}
                          disabled={approveMut.isPending}
                          data-testid={`approve-${u.username}`}
                        >
                          {I.check} Onayla
                        </button>
                        <button
                          type="button"
                          className="ico del"
                          onClick={() => rejectMut.mutate(u.id)}
                          disabled={rejectMut.isPending}
                          data-testid={`reject-${u.username}`}
                        >
                          {I.x}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ===== Kullanıcılar ===== */}
      <section className="card">
        <span className="cb3" /><span className="cb4" />
        <div className="ch-head">
          <div className="ch-ic">{I.shield}</div>
          <div className="ch-tt">
            <h2>
              Kullanıcılar <span className="n">({users?.length || 0})</span>
            </h2>
          </div>
          <div className="ch-note">Telegram kullanıcı adıyla otomatik kayıt</div>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Kullanıcı</th>
                <th>Rol</th>
                <th>Chat ID</th>
                <th>Kayıt</th>
                <th>Son Görülme</th>
                <th>Aktivite</th>
                <th>Durum</th>
                <th className="r">Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td>
                    <div className="svc">
                      <Avatar name={u.username} />
                      <span className="uname" data-testid={`username-${u.username}`}>@{u.username}</span>
                    </div>
                  </td>
                  <td>
                    {u.role === "admin" ? (
                      <span className="pill org" data-testid="role-pill-admin">
                        <span className="pd" />admin
                      </span>
                    ) : (
                      <span className="pill mut" data-testid="role-pill-user">
                        <span className="pd" />{u.role || "user"}
                      </span>
                    )}
                  </td>
                  <td><span className="mono" style={{ color: "var(--muted)" }}>{u.telegram_chat_id}</span></td>
                  <td><span className="dt">{fmtDate(u.created_at)}</span></td>
                  <td>
                    <span className="dt" style={{ color: u.last_seen ? "var(--txt)" : "var(--ghost)" }}>
                      {u.last_seen ? fmtDate(u.last_seen) : "—"}
                    </span>
                  </td>
                  <td>
                    <ActivityPill lastActive={u.last_seen} />
                  </td>
                  <td><span className="dash">—</span></td>
                  <td className="r">
                    <div className="row-act">
                      {u.username !== me?.username && (
                        <button
                          type="button"
                          className="ico del"
                          onClick={() => {
                            if (window.confirm(`@${u.username} silinsin mi?`))
                              delMut.mutate(u.id);
                          }}
                          data-testid={`delete-${u.username}`}
                        >
                          {I.trash}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== Oturumlar ===== */}
      <section className="card">
        <span className="cb3" /><span className="cb4" />
        <div className="ch-head">
          <div className="ch-ic">{I.radio}</div>
          <div className="ch-tt">
            <h2>
              Oturumlar <span className="n">({sessions?.length || 0})</span>
            </h2>
          </div>
          <button
            type="button"
            className="ch-note"
            onClick={() => setActiveOnly(!activeOnly)}
            data-testid="toggle-active-only"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              color: activeOnly ? "var(--up)" : "var(--faint)",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            <span
              className="pd"
              style={{
                width: 5, height: 5, borderRadius: "50%",
                background: activeOnly ? "var(--up)" : "var(--faint)",
                boxShadow: activeOnly ? "0 0 6px var(--up)" : "none",
              }}
            />
            {activeOnly ? "Sadece aktif · 15dk" : "Tüm oturumlar"}
          </button>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Kullanıcı</th>
                <th>Giriş Zamanı</th>
                <th>Son Aktivite</th>
                <th>Bitiş</th>
                <th className="r">Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {sessions?.map((s) => (
                <tr key={s.id} data-testid={`session-${s.id}`}>
                  <td>
                    <div className="svc">
                      <Avatar name={s.username} />
                      <span className="uname">@{s.username}</span>
                    </div>
                  </td>
                  <td><span className="dt">{fmtDate(s.created_at)}</span></td>
                  <td><span className="dt live">{fmtDate(s.last_active)}</span></td>
                  <td><span className="dt">{fmtDate(s.expires_at)}</span></td>
                  <td className="r">
                    <button
                      type="button"
                      className="end-btn"
                      onClick={() => revokeMut.mutate(s.id)}
                      data-testid={`revoke-${s.id}`}
                    >
                      Sonlandır
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
